<template>
    <div class="bg-color-granite pb-10">
        <div class="d-flex justify-center align-center w-100 position-relative">
            <v-img class="w-100 h-100"			
                :height="199"
                aspect-ratio="16/9"
                src="https://dellpartnerincentives.com/siteassets/Images/HomePartner/Banner/bgRISE2.jpg"
            ></v-img>
            <div class="position-absolute page-header">
                <h1><LanguagePlaceholder default-text="My Bonus Dashboard" /></h1>
            </div>			
        </div>
        <div class="bg-color-granite w-100 h-100">
            <div class="bg-color-quartz w-100 h-100 my-2">
                <v-row no-gutters class="d-flex justify-center w-100 pa-0 pt-3 bg-color-dell-blue tx-color-white">
                    <v-col cols="10" class="d-flex flex-column justify-start flex-column w-100 pa-8 tx-font-normal tx-font-size20">  
                        <div class="pb-10">
                            <LanguagePlaceholder  is-html
                                default-text="My Bonus Dashboard shows which bonuses you are eligible for" />
                        </div>

                        <div>
                            <v-select class="text-white w-25" v-model="selectedQuarter"
                                :items="quarters" variant="outlined"
                                aria-label="Select Incentive Period">
                                <template v-slot:label>                                                            	
                                    <LanguagePlaceholder is-html default-text="Select Incentive Period" />
                                </template>
                            </v-select>
                        </div>

                        <div class="ps-4">
                            <v-btn @click="clickSubmit" color="white" variant="outlined"><LanguagePlaceholder default-text="Submit" /></v-btn>
                        </div>
                    </v-col>
                </v-row>
            </div>
            <div class="bg-color-granite w-auto h-100 my-2 mx-10" >
                <v-row no-gutters class="d-flex justify-center w-100 pa-0">
                <v-col cols="1" class="bg-color-quartz"></v-col>
                <v-col cols="10" class="d-flex justify-start w-100 pa-0 py-10 tx-font-bold bg-color-quartz">  
                    <v-toolbar color="#EEEEEE">
                        <v-toolbar-title>
                            <h1 class="tx-color-grey pa-2">
                                <LanguagePlaceholder default-text="My Current Performance" />
                            </h1>                                            
                        </v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-btn icon="mdi-download" @click="downloadFile" size="large" aria-label="Download Report"><v-icon>mdi-download</v-icon></v-btn>
                    </v-toolbar>   
                </v-col>
                <v-col cols="1" class="bg-color-quartz"></v-col>
                </v-row>
                <v-row no-gutters class="bg-color-quartz d-flex justify-center w-100 pa-0 pb-5">
                    <v-col cols="1" class="bg-color-quartz"></v-col>
                    <v-col cols="10" class="bg-color-white d-flex justify-center w-100 pa-0">  
                        <div class="ma-0 pa-0 w-100">
                            <v-data-table items-per-page="50" :headers="headers" :items="reportData"
                                    item-key="id" class="w-100 v-data-table-row"
                                    role="table" aria-label="Partner bonus dashboard data">
                            </v-data-table>      
                        </div>            
                    </v-col>
                    <v-col cols="1" class="bg-color-quartz"></v-col>
                </v-row>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeMount } from 'vue'
import { useSystemStore } from '@/stores/system'
import { useReportsStore } from '@/stores/reports'
import { useUserStore } from '@/stores/user'
import { useExcelStore } from '@/stores/excel'

import LanguagePlaceholder from '@/components/LanguagePlaceholder.vue'

const systemStore = useSystemStore()
const reportsStore = useReportsStore()
const userStore = useUserStore()
const excelStore = useExcelStore()

const selectedQuarter = ref('')

const quarters = ref([])

const reportData = ref([])
const headers = ref([])
const selectedReport = ref({})
const searchString = ref('')
const userId = ref(0)

const defineHeaders = () => {

    if (systemStore.userDetail.userType.trim() != 'Partner') {
        headers.value.push(
        {
            title: `AffinityId`,
            value: `partnerAffinityId`,
            headerProps: { class: 'v-data-table-header' },
            sortable: true
        })

        headers.value.push(
        {
            title: `Partner`,
            value: `partnerName`,
            headerProps: { class: 'v-data-table-header' },
            sortable: true
        })
    }

    headers.value.push(
    {
        title: `Bonus Name`,
        value: `bonusName`,
        headerProps: { class: 'v-data-table-header' },
        sortable: true
    })
    
    headers.value.push(
    {
        title: `Units`,
        value: `units`,
        headerProps: { class: 'v-data-table-header' },
        sortable: true
    })

    headers.value.push(
    {
        title: `Unit Threshold`,
        value: `unitThresholdAndRewardValue`,
        headerProps: { class: 'v-data-table-header' },
        sortable: true
    })

    headers.value.push(
    {
        title: `Bonus Value`,
        value: `bonusValue`,
        headerProps: { class: 'v-data-table-header' },
        sortable: true
    })

    headers.value.push(
    {
        title: `Qualified`,
        value: `qualified`,
        headerProps: { class: 'v-data-table-header' },
        sortable: true
    })

    headers.value.push(
    {
        title: `Reasons`,
        value: `reasons`,
        headerProps: { class: 'v-data-table-header' },
        sortable: true
    })
}

onBeforeMount(() => {
    defineHeaders()

    getBonusReportTemplate()
})

onMounted(() => {
    userStore.getUserId().then(result => {
        userId.value = result.responseData  
    }).catch(error => {
        console.log('getUserId', error)
    })

    // No access restrictions required since the partner needs to see the current quarter
    //
    //const dellInternalReportAccess = ['TIC_Admin', 'TP', 'PAM', 'Dell Marketing']   
    //const hasDellInternalReportAccess = dellInternalReportAccess.indexOf(systemStore.userDetail.userType.trim()) > -1 ? true : false

    systemStore.getQuarters().then(result => {
        if (result.success) {
            // if (hasDellInternalReportAccess) {
            //     quarters.value = result.quarters
            //         .filter(q => q.currentQuarters === true)
            //         .map(q => q.quarter)
            // } else {
            //     quarters.value = result.quarters
            //         .filter(q => q.currentQuarters === true && q.currentQuarter === false)
            //         .map(q => q.quarter)
            // }
            
            quarters.value = result.quarters
                    .filter(q => q.currentQuarters === true)
                    .map(q => q.quarter)
            
           selectedQuarter.value = quarters.value[0]

           setTimeout(() => {
            getReportData()
          }, 2000)           
        }
    }).catch(error => {
        console.log('getQuarters', error)
    })    
})

const downloadFile = () => {
    if (reportData.value.length === 0) {
        return
    }

    excelStore.generateInMemoryExcelDocumentV2(reportData.value, 'Bonus Report').catch(error => {
        console.log('Generate In Memory Excel Document', error)
    })
}

const clickSubmit = () => {
    getReportData()
}

const getBonusReportTemplate = () => {
    reportsStore.getReports(':BonusReport:').then(result => {
        if (result.success === true) {
            if (result.hasData) {
                selectedReport.value = JSON.parse(result.reportData)[0];
                if (selectedReport.value.Params.length > 0) {
                    selectedReport.value.Params = JSON.parse(selectedReport.value.Params) 
                }
            }
        }
    }).catch(error => {
        console.log(`getBonusReportTemplate`, error)
    })
}

const getReportData = () => {

    if (selectedReport.value.Params.length > 0) {
        selectedReport.value.Params.forEach((p) => {
            if (p.Name === 'Quarter') {
                p.Value = selectedQuarter.value
            }
            else if (p.Name === 'UserId') {
                p.Value = userId.value
            }
        })
    }
    
    reportData.value = []

    reportsStore.getReportData(selectedReport.value, false, false).then(result => {
    if (result.success === true) {
        if (result.hasData) {
            reportData.value = JSON.parse(result.reportData)
        }            
    }
    }).catch(error => {
        console.log(`getReportData`, error)
    })
}
</script>